import { useState, useMemo } from 'react';
import { Box, Button, Typography, CircularProgress, Stack } from '@mui/material';
import {
    PersonAdd as PersonAddIcon,
    SupervisorAccount as SupervisorAccountIcon,
    Category as CategoryIcon,
    Event as EventIcon,
    BarChart as BarChartIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { StatsCards } from '@/components/admin/StatsCards';
import { FilterBar } from '@/components/admin/FilterBar';
import { UserCard } from '@/components/admin/UserCard';
import { AddUserModal } from '@/components/admin/AddUserModal';
import { AddProxyModal } from '@/components/admin/AddProxyModal';
import { EmptyState } from '@/components/admin/EmptyState';
import {
    useGetAdminDataQuery,
    useGetSettingsQuery,
    useAddResourceMutation,
    useUpdateResourceMutation,
    useDeleteResourceMutation,
    useAddResourceCategoryMutation,
    useUpdateResourceCategoryMutation,
    useDeleteResourceCategoryMutation,
    useAddCategoryResponsibleMutation,
    useDeleteCategoryResponsibleMutation,
    useAddResourceProxyMutation,
    useDeleteResourceProxyMutation,
} from '@/store/apiSlice';
import { useCategoriesResources } from '@/store/hooks';
import { useI18nContext } from '@/i18n/i18n-react';
import { useAppDialog } from '@/components/dialogs/AppDialog';
import { getApiErrorMessage } from '@/utils/apiError';

const AdminPage = () => {
    const navigate = useNavigate();
    const { LL } = useI18nContext();
    const { alert, confirm } = useAppDialog();
    const [filterMode, setFilterMode] = useState<'all' | 'active' | 'inactive' | 'admin'>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [addUserModalOpen, setAddUserModalOpen] = useState(false);
    const [addProxyModalOpen, setAddProxyModalOpen] = useState(false);

    const [newUser, setNewUser] = useState({
        name: '',
        mail: '',
        auth: '',
        active: true,
        admin: false,
        scheduleEditor: false,
        scheduleTemplateEditor: false,
        eventEditor: false,
    });

    const [selectedProxy, setSelectedProxy] = useState<number>(0);

    const { data: adminData, isLoading } = useGetAdminDataQuery();
    const categories = useCategoriesResources();
    const { data: settings } = useGetSettingsQuery();

    const authCodeEnabled: boolean =
        settings?.auth?.authCodeEnabled !== undefined ? settings.auth.authCodeEnabled : true;
    const scheduleEnabled: boolean = settings?.schedule?.enabled ?? false;
    const oidcAdminGroup: string | null | undefined = settings?.auth?.oidcAdminGroup;

    const [addResource] = useAddResourceMutation();
    const [updateResource] = useUpdateResourceMutation();
    const [deleteResource] = useDeleteResourceMutation();
    const [addResourceCategory] = useAddResourceCategoryMutation();
    const [updateResourceCategory] = useUpdateResourceCategoryMutation();
    const [deleteResourceCategory] = useDeleteResourceCategoryMutation();
    const [addCategoryResponsible] = useAddCategoryResponsibleMutation();
    const [deleteCategoryResponsible] = useDeleteCategoryResponsibleMutation();
    const [addResourceProxy] = useAddResourceProxyMutation();
    const [deleteResourceProxy] = useDeleteResourceProxyMutation();

    const resources = useMemo(() => {
        if (!adminData?.resources) return [];
        return [...adminData.resources].sort((a, b) => a.name.localeCompare(b.name));
    }, [adminData]);

    const filteredResources = useMemo(() => {
        let filtered = resources;

        switch (filterMode) {
            case 'active':
                filtered = filtered.filter((user) => user.active === 1);
                break;
            case 'inactive':
                filtered = filtered.filter((user) => user.active === 0);
                break;
            case 'admin':
                filtered = filtered.filter((user) => user.admin === 1);
                break;
        }

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(
                (user) =>
                    user.name.toLowerCase().includes(query) ||
                    user.mail?.toLowerCase().includes(query)
            );
        }

        return filtered;
    }, [resources, filterMode, searchQuery]);

    const handleAddUser = async () => {
        try {
            await addResource(newUser).unwrap();
            setAddUserModalOpen(false);
            setNewUser({
                name: '',
                mail: '',
                auth: '',
                active: true,
                admin: false,
                scheduleEditor: false,
                scheduleTemplateEditor: false,
                eventEditor: false,
            });
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    const handleUpdateUser = async (userId: number, field: string, value: any) => {
        const user = resources.find((r) => r.id === userId);
        if (!user) return;

        try {
            await updateResource({
                id: userId,
                name: user.name,
                mail: user.mail,
                auth: user.auth,
                active: user.active,
                admin: user.admin,
                scheduleEditor: user.scheduleEditor,
                scheduleTemplateEditor: user.scheduleTemplateEditor,
                eventEditor: user.eventEditor,
                [field]: value,
            }).unwrap();
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    const handleDeleteUser = async (userId: number) => {
        if (!(await confirm(LL.adminPage.deleteConfirm()))) return;

        try {
            await deleteResource({ id: userId }).unwrap();
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    const handleCategory = async (
        userId: number,
        categoryId: number,
        state: 'member' | 'assistant' | 'none'
    ) => {
        const isInCategory = adminData?.categoriesResources?.[userId]?.[categoryId];

        try {
            if (isInCategory) {
                if (state === 'none') {
                    await deleteResourceCategory({
                        resource: userId,
                        category: categoryId,
                    }).unwrap();
                } else {
                    await updateResourceCategory({
                        resource: userId,
                        category: categoryId,
                        assistant: state === 'assistant',
                    }).unwrap();
                }
            } else {
                if (state !== 'none') {
                    await addResourceCategory({
                        resource: userId,
                        category: categoryId,
                        assistant: state === 'assistant',
                    }).unwrap();
                }
            }
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    const handleResponsible = async (userId: number, categoryId: number, responsible: boolean) => {
        const user = resources.find((r) => r.id === userId);
        const isResponsible = user?.categories.includes(categoryId.toString());

        if (isResponsible === responsible) {
            return;
        }

        try {
            if (isResponsible) {
                await deleteCategoryResponsible({
                    resource: userId,
                    category: categoryId,
                }).unwrap();
            } else {
                await addCategoryResponsible({ resource: userId, category: categoryId }).unwrap();
            }
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    const handleAddProxy = async () => {
        if (!selectedUserId || !selectedProxy) return;

        try {
            await addResourceProxy({ resource: selectedUserId, proxy: selectedProxy }).unwrap();
            setAddProxyModalOpen(false);
            setSelectedProxy(0);
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    const handleDeleteProxy = async (userId: number, proxyId: number) => {
        try {
            await deleteResourceProxy({ resource: userId, proxy: proxyId }).unwrap();
        } catch (error) {
            await alert(getApiErrorMessage(error));
        }
    };

    if (isLoading) {
        return (
            <Stack
                sx={{
                    direction: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    gap: 2,
                }}
            >
                <CircularProgress size={60} />
                <Typography variant="h6" color="text.secondary">
                    {LL.mainPage.loadingAdminData()}
                </Typography>
            </Stack>
        );
    }

    return (
        <>
            <AdminPageHeader
                icon={<SupervisorAccountIcon />}
                title={LL.adminPage.title()}
                subtitle={LL.adminPage.subtitle()}
                backPath="/"
                backLabel={LL.adminPage.backToPlan()}
                actions={
                    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<CategoryIcon />}
                            onClick={() => navigate('/admin/categories')}
                        >
                            {LL.adminPage.categories()}
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<EventIcon />}
                            onClick={() => navigate('/admin/events')}
                        >
                            {LL.adminEventsPage.events()}
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<BarChartIcon />}
                            onClick={() => navigate('/admin/metrics')}
                        >
                            {LL.metricsPage.title()}
                        </Button>

                        {authCodeEnabled && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<PersonAddIcon />}
                                onClick={() => setAddUserModalOpen(true)}
                            >
                                {LL.adminPage.addUser()}
                            </Button>
                        )}
                    </Stack>
                }
            />

            <Box sx={{ px: 3 }}>
                <StatsCards
                    totalUsers={resources.length}
                    activeUsers={resources.filter((u) => u.active === 1).length}
                    admins={resources.filter((u) => u.admin === 1).length}
                    categories={categories.length}
                />

                <FilterBar
                    filterMode={filterMode}
                    onFilterModeChange={setFilterMode}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                />

                <Stack sx={{ gap: 2 }}>
                    {filteredResources.map((user) => (
                        <UserCard
                            key={user.id}
                            user={user}
                            isExpanded={selectedUserId === user.id}
                            onExpand={() =>
                                setSelectedUserId(selectedUserId === user.id ? null : user.id)
                            }
                            onUpdateUser={handleUpdateUser}
                            onDeleteUser={handleDeleteUser}
                            onCategory={handleCategory}
                            onResponsible={handleResponsible}
                            onDeleteProxy={handleDeleteProxy}
                            onAddProxy={() => {
                                setSelectedUserId(user.id);
                                setAddProxyModalOpen(true);
                            }}
                            categories={categories || []}
                            categoryResources={adminData?.categoriesResources?.[user.id] || {}}
                            allResources={resources}
                            authCodeEnabled={authCodeEnabled}
                            scheduleEnabled={scheduleEnabled}
                            oidcAdminGroup={oidcAdminGroup}
                        />
                    ))}
                </Stack>

                {filteredResources.length === 0 && (
                    <EmptyState
                        filterMode={filterMode}
                        onAddUser={() => setAddUserModalOpen(true)}
                    />
                )}

                {authCodeEnabled && (
                    <AddUserModal
                        open={addUserModalOpen}
                        onClose={() => setAddUserModalOpen(false)}
                        onSubmit={handleAddUser}
                        newUser={newUser}
                        onUserChange={setNewUser}
                        authCodeEnabled={authCodeEnabled}
                    />
                )}

                <AddProxyModal
                    open={addProxyModalOpen}
                    onClose={() => setAddProxyModalOpen(false)}
                    onSubmit={handleAddProxy}
                    selectedProxy={selectedProxy}
                    onProxyChange={setSelectedProxy}
                    resources={resources}
                    currentUserId={selectedUserId}
                />
            </Box>
        </>
    );
};

export default AdminPage;
